import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import { useHubExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';
import { usePersistentValue } from '../hooks/usePersistentState';
import { resolveTabs } from '../i18n/resolveTabs';
import CapacityAnalysisPage from './tdabc/CapacityAnalysisPage';
import CapacityBillableTime from './tdabc/CapacityBillableTime';
import CoutProductionPrint from './tdabc/CoutProductionPrint';
import InfoTooltip from '../components/InfoTooltip';

/* Coût de production (module Rentabilité) — section 1 : contrôle de productivité
   et coût de la capacité. Trois onglets :
     • Usage temps         → utilisation du temps d'ouverture (productif/structure/non-productif).
     • Coût capacité       → métriques par personne / groupe : EPT capa (TR/TO),
                             CCR, PCR (ex-BCR), coût horaire non-productif, coût inutilisé.
   Les onglets legacy « Standard annuel » / « Réalisé » (ChrPage PCR) ont été retirés
   le 2026-07-13 : doublons de « Coût capacité », et le mode standard n'avait plus de
   source. La distinction Standard vs Réel reviendra avec le stockage Standard distinct.
   Barre d'onglets (aside) : bascule CHF ⇄ kCHF (onglet « Coût production ») + export
   PDF + export Excel — format standard des hubs.
   Gabarit unique : <PageShell framed> + <Tabs> + sous-pages en mode embedded. */

/* « Temps de production » est parti dans « Saisie des temps » le 2026-08-30 :
   c'était le seul onglet de SAISIE au milieu de deux lectures. */
const TAB_DEFS = [
  { id: 'temps-fact',  labelKey: 'rentabilite:production.tabs.temps-fact' },
  { id: 'capacite',    labelKey: 'rentabilite:production.tabs.capacite' },
];

// Onglet à montants (bascule CHF/kCHF pertinente).
const MONEY_TABS = new Set(['capacite']);

export default function CoutHeureTFHub({ activeTenant, addToast, year }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [tab, setTab] = usePersistentValue(`cout_prod_tab_${tenantId}`, 'capacite');
  const TABS = resolveTabs(TAB_DEFS, t);
  const [unit, setUnit] = usePersistentValue(`cout_prod_unit_${tenantId}`, 'kchf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const [showPrint, setShowPrint] = useState(false);

  // Rendu d'un onglet — à l'écran (unité du hub) et pour l'export Excel (CHF forcé).
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'temps-fact': return <CapacityBillableTime tenantId={tenantId} year={year} />;
      case 'capacite':   return <CapacityAnalysisPage embedded tablesOnly moneyUnit={u} activeTenant={activeTenant} addToast={addToast} year={year} />;
      default: return null;
    }
  };

  // Exports (PDF · Excel) → registre, rendus par l'icône télécharger de la barre
  // supérieure. La rangée d'onglets ne garde que la bascule d'unité.
  const excel = useHubExcelExport({
    tabs: TABS.map(tb => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: t('phrases:p_6edef097ac'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'pdf', label: 'PDF', run: () => setShowPrint(true) },
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  const viewControls = null;   // l'unité vit dans la bande de contexte

  return (
    <PageShell framed kind="report" unit={MONEY_TABS.has(tab) ? pageUnit : null} title={<>{t('rentabilite:production.title')}<InfoTooltip term="pcr" /></>}>
      {/* Plus de flèche `flow` : les deux onglets ne se construisent pas l'un
          l'autre, ce sont deux lectures (2026-09-01). */}
      <Tabs tabs={TABS} active={tab} onChange={setTab} aside={viewControls} />

      <div className="page-shell__panel">
        {renderTab(tab)}
      </div>
      {excel.stage}

      {showPrint && (
        <CoutProductionPrint activeTenant={activeTenant} year={year} unit={unit} onClose={() => setShowPrint(false)} />
      )}
    </PageShell>
  );
}

